/**
 * Packing-slip order QR payload for London Smoke & Cure.
 * Format: LSC:ORDER:{sourceId}  (Order Desk source / order number, no leading #)
 */

export const ORDER_QR_PREFIX = 'LSC:ORDER:';

/** @param {string|number|null|undefined} sourceId */
export function normalizeOrderQrSourceId(sourceId) {
  return String(sourceId ?? '')
    .trim()
    .replace(/^#/, '')
    .trim();
}

/**
 * @param {string|number|null|undefined} sourceId
 * @returns {string} empty string if no id
 */
export function formatOrderQrPayload(sourceId) {
  const id = normalizeOrderQrSourceId(sourceId);
  if (!id) return '';
  return `${ORDER_QR_PREFIX}${id}`;
}

/**
 * Parse wedge / camera text into an order source id when it is an LSC order QR.
 * Also accepts a bare numeric-looking order id when `allowBareId` is true (wedge mistype recovery off by default).
 * @param {string} raw
 * @param {{ allowBareId?: boolean }} [opts]
 * @returns {string|null}
 */
export function parseOrderQrPayload(raw, opts = {}) {
  const text = String(raw ?? '').trim();
  if (!text) return null;

  const upper = text.toUpperCase();
  if (upper.startsWith(ORDER_QR_PREFIX)) {
    const id = normalizeOrderQrSourceId(text.slice(ORDER_QR_PREFIX.length));
    return id || null;
  }

  // Deep-link style: …?order=260722805 or …#order=…
  try {
    if (/^https?:\/\//i.test(text)) {
      const url = new URL(text);
      const fromQuery = url.searchParams.get('order') || url.searchParams.get('source_id');
      if (fromQuery) return normalizeOrderQrSourceId(fromQuery) || null;
    }
  } catch {
    /* ignore */
  }

  if (opts.allowBareId) {
    const bare = normalizeOrderQrSourceId(text);
    if (/^\d{6,}$/.test(bare)) return bare;
  }

  return null;
}

/**
 * Resolve which loaded order matches a scanned source id.
 * @param {Array} orders
 * @param {string} sourceId
 */
export function findOrderByQrSourceId(orders, sourceId) {
  const target = normalizeOrderQrSourceId(sourceId).toUpperCase();
  if (!target || !Array.isArray(orders)) return null;

  const candidates = (order) =>
    [
      order?.source_id,
      order?.sourceId,
      order?.orderNumber,
      order?.order_number,
      order?.orderId,
      order?.order_id,
      order?.id,
      order?.['Order ID'],
      order?.['Source ID#'],
    ]
      .map((v) => normalizeOrderQrSourceId(v).toUpperCase())
      .filter(Boolean);

  return (
    orders.find((order) => candidates(order).some((id) => id === target)) || null
  );
}

/**
 * Stable key for selection sets (prefer source id / order number).
 */
export function getOrderSelectionKey(order) {
  if (!order) return '';
  return (
    normalizeOrderQrSourceId(
      order.source_id ||
        order.sourceId ||
        order.orderNumber ||
        order.order_number ||
        order.orderId ||
        order.order_id ||
        order.id ||
        order['Order ID'] ||
        '',
    )
  );
}
